import { useCallback, useEffect } from 'react';
import isEmpty from 'lodash/isEmpty';
import { LogoutOptions, useAuth0 } from '@auth0/auth0-react';
import PiInstance from 'Shared/api/Pi.instance';
import { useCurrentUser } from 'Shared/utils/hooks/use-current-user';
import { useApp } from 'Shared/utils/hooks/use-app';

const SyncPredictiveIndexAuth = () => {
  const { user, isPiUser, isImpersonated } = useCurrentUser();
  const { app, isZoom } = useApp();

  const { isLoading, logout, getAccessTokenSilently, getAccessTokenWithPopup } = useAuth0();

  const piSignInAllowed: boolean = JSON.parse(app.piSignInAllowed.toLowerCase());
  const piAllowedEmailDomains: string = app.piAllowedEmailDomains || '';
  const piBlockedEmailDomains: string = app.piDisallowedEmailDomains || '';

  const getEmailClaimFromToken = (token: string): string => {
    if (!token) return null;
    return JSON.parse(atob(token.split('.')[1]))['https://www.predictiveindex.com/email'];
  };

  const isUserAllowedToSignIn = useCallback(
    (email: string): boolean => {
      const domain = email.split('@')[1];
      const allowedDomains = piAllowedEmailDomains.split(',');
      if (!piSignInAllowed && !allowedDomains.includes(domain)) {
        return false;
      }

      // block list added:
      // For some paid perform customers with stripe who happen to be paid pi customers,
      // we need to hold off on having them link their accounts until their stripe subscription has been deleted
      const blockedDomains = piBlockedEmailDomains.split(',');
      if (blockedDomains.includes(domain)) {
        return false;
      }

      return true;
    },
    [piAllowedEmailDomains, piSignInAllowed]
  );

  const auth0Logout = async () => {
    try {
      await logout({
        logoutParams: {
          returnTo: window.origin + app.routes.destroyUserSession,
        },
      } as LogoutOptions);
    } catch (e) {
      console.log(e);
    }
  };

  useEffect(() => {
    PiInstance.setTokenFunc(getAccessTokenSilently);
  }, [getAccessTokenSilently]);

  useEffect(() => {
    if (isLoading) return;

    // Impersonation is going to cause a disconnect between the JWT user and the session user
    // so we'll turn everything off if impersonation is on
    if (isImpersonated) return;

    // The Zoom App is not interacting with Auth0 right now, so we'll turn everything off
    if (isZoom) return;

    const fetchTokenSilently = async (bustCache = false) => {
      if (bustCache) {
        return await getAccessTokenSilently({ cacheMode: 'off' });
      }

      return await getAccessTokenSilently();
    };

    const fetchData = async (retry = false) => {
      let jwt = '';

      try {
        jwt = await fetchTokenSilently(retry);
      } catch (e) {
        if (e['error'] === 'consent_required' && user && isPiUser && window.location.hostname.includes('localhost')) {
          jwt = await getAccessTokenWithPopup();
        } else {
          console.log('getAccessTokenSilently failed with error', e);
          // Swallow this, until we can do the migration and all our users have a unified IDP
        }
      } finally {
        const emailFromJWT = getEmailClaimFromToken(jwt);
        console.log('emailFromJWT', emailFromJWT);
        if (emailFromJWT && isUserAllowedToSignIn(emailFromJWT) && isEmpty(user)) {
          window.location.href = `/token_auth/pi_auth0_login`;
        }

        if (!isEmpty(user)) {
          if (emailFromJWT && isPiUser && user.email.toLowerCase() !== emailFromJWT.toLowerCase()) {
            console.log('Token Email and Session Mismatch', emailFromJWT, 'vs', user.email);
            auth0Logout();
          } else if (!emailFromJWT && isPiUser && isUserAllowedToSignIn(user.email)) {
            console.log('Unable to fetch JWT. Autologout triggered second try=', retry);
            if (!retry) {
              // if this was the first attempt, wait for 10 seconds and retry
              setTimeout(() => fetchData(true), 10000);
            } else {
              // if the attempt has already been retried, autologout
              auth0Logout();
            }
          }
        }
      }
    };

    fetchData();
  }, [isLoading, getAccessTokenSilently, isImpersonated, user, isUserAllowedToSignIn, isPiUser]);

  return null;
};

export default SyncPredictiveIndexAuth;
